import React, { useCallback, useEffect, useRef, useState } from "react";
import "./AdminDashboard.css";
import { BBW_INSIGHTS_HREF, BBW_INSIGHTS_ENABLED, BBW_INSIGHTS_LABEL, BBW_INSIGHTS_SIGN_IN_NOTICE } from "../lib/bbwInsights";
import useAdminOperation from "../hooks/useAdminOperation";
import { Link, useSearchParams } from "react-router-dom";
import { shippingIssues, fulfillmentIssues, reconcileIssueSnapshot, inventoryItemRoute } from "../lib/adminIssues";
import { toast } from "sonner";
import { api, API, formatApiError, getToken, captureSession, isSessionCurrent } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { STATUS_OPTIONS } from "../data/siteContent";
import { ChevronDown, Download, Mail, Phone } from "lucide-react";
import AdminImageWithFallback from "../components/admin/AdminImageWithFallback";
import AdminSummaryCard from "../components/admin/AdminSummaryCard";
import PriorityAccordion from "../components/admin/PriorityAccordion";
import PriorityOrderCard from "../components/admin/PriorityOrderCard";
import AdminShipmentLabels from "../components/admin/AdminShipmentLabels";

function statusColor(status) {
  switch (status) {
    case "New Request":
      return "bg-[#D8A34B]/20 text-[#D8A34B] border-[#D8A34B]/40";
    case "Needs Follow-Up":
      return "bg-rose-700/20 text-rose-400 border-rose-700/40";
    case "Quote Sent":
      return "bg-[#26C6D5]/20 text-[#26C6D5] border-[#26C6D5]/40";
    case "Approved":
      return "bg-emerald-700/20 text-emerald-400 border-emerald-700/40";
    case "In Design":
      return "bg-violet-700/20 text-violet-400 border-violet-700/40";
    case "In Production":
      return "bg-indigo-700/20 text-indigo-400 border-indigo-700/40";
    case "Completed":
      return "bg-stone-700/40 text-stone-300 border-stone-700/40";
    case "Archived":
      return "bg-zinc-700/40 text-zinc-300 border-zinc-700/40";
    default:
      return "bg-stone-700/20 text-stone-300 border-stone-700/40";
  }
}

function ShippingAddressCorrection({ order, onSaved, busy, setBusy }) {
  const beginOperation = useAdminOperation();
  const [address, setAddress] = useState(order.shipping_address || {});
  const fields = [
    ["name", "Recipient"], ["phone", "Phone"], ["address_1", "Address line 1"],
    ["address_2", "Address line 2"], ["city", "City"], ["state", "State"],
    ["postal_code", "ZIP code"], ["country", "Country code"],
  ];
  const save = async (event) => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    event.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/orders/${order.id}/shipping/address`, address);
    if (!isCurrentOperation()) return;
      await onSaved();
    if (!isCurrentOperation()) return;
      toast.success("Corrected address validated. Recalculate rates before label purchase.");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    } finally {
      if (isCurrentOperation()) {
        setBusy(false);
      }
    }
  };
  return (
    <form onSubmit={save} className="mt-3 space-y-2 border border-amber-400/30 p-3" data-testid="shipping-address-correction">
      <p className="text-sm text-amber-200">Review the address Square returned. Saving validates it and requires a fresh rate.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {fields.map(([key, label]) => <label key={key} className="text-xs text-stone-400">{label}<input required={!["phone", "address_2"].includes(key)} value={address[key] || ""} onChange={(event) => setAddress((current) => ({ ...current, [key]: event.target.value }))} className="mt-1 w-full rounded border border-stone-100/15 bg-zinc-950 p-2 text-sm text-stone-100" /></label>)}
      </div>
      <Button type="submit" disabled={busy}>{busy ? "Validating..." : "Validate corrected address"}</Button>
    </form>
  );
}

export default function Admin() {
  const beginOperation = useAdminOperation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedOrder = searchParams.get("order");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);
  const [shippingChecks, setShippingChecks] = useState(null);
  const shippingCheckSequence = useRef(0);
  const dashboardSequence = useRef(0);
  const orderSelection = useRef(0);
  const beginOrderOperation = () => { const current = beginOperation(); const selection = orderSelection.current; return () => current() && selection === orderSelection.current; };
  const [shippingChecking, setShippingChecking] = useState(true);
  const [shippingCheckError, setShippingCheckError] = useState("");
  const [approvedTotalDrafts, setApprovedTotalDrafts] = useState({});
  const [galleryItems, setGalleryItems] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [events, setEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [fulfillmentAlerts, setFulfillmentAlerts] = useState(null);
  const [fulfillmentIssueSnapshot, setFulfillmentIssueSnapshot] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [commerceOrders, setCommerceOrders] = useState([]);
  const [orderActionReason, setOrderActionReason] = useState("");
  const [refundPreview, setRefundPreview] = useState(null);
  const [productionAcknowledged, setProductionAcknowledged] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [shippingQuoteId, setShippingQuoteId] = useState("");
  const [shippingBusy, setShippingBusy] = useState(false);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [orderAlertsOpen, setOrderAlertsOpen] = useState(false);
  const [ackBusy, setAckBusy] = useState("");
  const [quoteTab, setQuoteTab] = useState("active");
  const [quoteSearch, setQuoteSearch] = useState("");
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupConfirmText, setCleanupConfirmText] = useState("");
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const ACTIVE_QUOTE_STATUSES = ["New Request", "Needs Follow-Up", "Quote Sent", "Approved", "In Design", "In Production"];
  const filteredQuotes = quotes.filter((quote) => {
    if (!`${quote.customer_name || ""} ${quote.project_type || ""} ${quote.id || ""}`.toLowerCase().includes(quoteSearch.trim().toLowerCase())) return false;
    if (quoteTab === "active") return ACTIVE_QUOTE_STATUSES.includes(quote.status);
    if (quoteTab === "completed") return quote.status === "Completed";
    if (quoteTab === "archived") return quote.status === "Archived";
    return true;
  });
  const quoteCounts = {
    active: quotes.filter((q) => ACTIVE_QUOTE_STATUSES.includes(q.status)).length,
    completed: quotes.filter((q) => q.status === "Completed").length,
    archived: quotes.filter((q) => q.status === "Archived").length,
    all: quotes.length,
  };

  // Active order-action alerts. The backend now returns only active records, but
  // also guard against resolved/acknowledged statuses client-side so historical
  // failures never reappear. Scoped to the selected order inside Order actions.
  const activeNotifications = notifications.filter((n) => !["resolved", "acknowledged"].includes(n.status));
  const selectedOrderNotifications = selectedOrder
    ? activeNotifications.filter((n) => n.order_id === selectedOrder.id)
    : [];
  const orderAlertCount = selectedOrder ? selectedOrderNotifications.length : activeNotifications.length;

  const previewTestCleanup = async () => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    setCleanupBusy(true);
    try {
      const response = await api.post("/admin/quotes/cleanup-test", { confirm: false });
    if (!isCurrentOperation()) return;
      setCleanupPreview(response.data);
      setCleanupConfirmText("");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    } finally {
      if (isCurrentOperation()) {
        setCleanupBusy(false);
      }
    }
  };

  const executeTestCleanup = async () => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    if (cleanupConfirmText !== "DELETE TEST QUOTES") {
      toast.error("Type DELETE TEST QUOTES to confirm.");
      return;
    }
    setCleanupBusy(true);
    try {
      const ids = (cleanupPreview?.candidates || []).map((c) => c.id);
      const response = await api.post("/admin/quotes/cleanup-test", { confirm: true, ids, confirmation_text: cleanupConfirmText });
    if (!isCurrentOperation()) return;
      toast.success(`Deleted ${response.data.deleted} test quote(s).`);
      setCleanupPreview(null);
      setCleanupConfirmText("");
      await load();
    if (!isCurrentOperation()) return;
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    } finally {
      if (isCurrentOperation()) {
        setCleanupBusy(false);
      }
    }
  };

  const acknowledgeAlert = async (alertId) => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    if (!window.confirm("Acknowledge this alert? It moves to history and is never deleted.")) return;
    setAckBusy(alertId);
    try {
      await api.post(`/admin/fulfillment-alerts/${encodeURIComponent(alertId)}/acknowledge`);
    if (!isCurrentOperation()) return;
      await load();
    if (!isCurrentOperation()) return;
      toast.success("Alert acknowledged and moved to history.");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    } finally {
      if (isCurrentOperation()) {
        setAckBusy("");
      }
    }
  };

  const loadAlertHistory = async () => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    try {
      const response = await api.get("/admin/fulfillment-alerts/history", { params: { limit: 50 } });
    if (!isCurrentOperation()) return;
      setAlertHistory(response.data || []);
      setHistoryOpen(true);
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const loadShippingChecks = useCallback(async () => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    const requestId = ++shippingCheckSequence.current;
    const scope = captureSession();
    setShippingChecking(true);
    try {
      const [readiness, settings] = await Promise.all([api.get("/admin/shipping/readiness"), api.get("/admin/shipping/settings")]);
    if (!isCurrentOperation()) return;
      if (requestId !== shippingCheckSequence.current || !isSessionCurrent(scope)) return;
      const checkedAt = new Date().toISOString();
      const issues = shippingIssues(readiness.data?.items, settings.data, checkedAt, readiness.data);
      setShippingChecks((previous) => ({items: readiness.data.items, settings: settings.data, ...reconcileIssueSnapshot(previous,{source:"shipping-readiness",issues,checkedAt,status:"success",authoritative:true,complete:true})}));
      setShippingCheckError("");
    } catch (error) {
    if (!isCurrentOperation()) return;
      if (requestId !== shippingCheckSequence.current || !isSessionCurrent(scope)) return;
      setShippingCheckError(formatApiError(error));
      setShippingChecks((previous) => previous ? {...previous,...reconcileIssueSnapshot(previous,{source:"shipping-readiness",status:"error",error:formatApiError(error)})} : null);
    } finally {
      if (isCurrentOperation()) {
        if (requestId === shippingCheckSequence.current && isSessionCurrent(scope)) setShippingChecking(false);
      }
    }
  }, [beginOperation]);
  const shippingMissing = (shippingChecks?.issues || []).filter((entry) => entry.kind === "item");
  const shippingSetupIssues = (shippingChecks?.issues || []).filter((entry) => entry.kind === "setup");
  const retainedVerificationIssues = (fulfillmentIssueSnapshot?.issues || []).filter((entry) => entry.status === "verification_required" || entry.resolutionPending);
  const renderShippingIssue = (entry) => <article className="bbw-priority-row" key={entry.key}><div><strong>{entry.title}</strong><p>{entry.reason}</p></div><Link className="bbw-fix-link" aria-label={`Fix shipping for ${entry.title}`} to={entry.actionRoute}>Fix this →</Link></article>;
  const load = useCallback(async () => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    const scope = captureSession();
    const sequence = ++dashboardSequence.current;
    const shippingCheckRequest = loadShippingChecks();
    setLoading(true);
    setLoadError("");
    try {
      const [quotesResponse, notificationsResponse, fulfillmentResponse, ordersResponse] =
        await Promise.all([
          api.get("/admin/quotes"),
          api.get("/admin/notifications"),
          api.get("/admin/fulfillment-alerts"),
          api.get("/admin/orders"),
        ]);
    if (!isCurrentOperation()) return;
      if (sequence !== dashboardSequence.current || !isSessionCurrent(scope)) return;
      const checkedAt = new Date().toISOString();
      const issues = fulfillmentIssues(fulfillmentResponse.data, checkedAt);
      setQuotes(quotesResponse.data);
      setNotifications(notificationsResponse.data || []);
      setFulfillmentAlerts(fulfillmentResponse.data);
      setFulfillmentIssueSnapshot((previous) => reconcileIssueSnapshot(previous,{source:"fulfillment-alerts",issues,checkedAt,status:"success",authoritative:false,complete:true}));
      setCommerceOrders(ordersResponse.data || []);
      setHasLoaded(true);
    } catch (err) {
    if (!isCurrentOperation()) return;
      if (sequence !== dashboardSequence.current || !isSessionCurrent(scope)) return;
      setLoadError(formatApiError(err));
      setFulfillmentIssueSnapshot((previous) => previous ? reconcileIssueSnapshot(previous,{source:"fulfillment-alerts",status:"error",error:formatApiError(err)}) : null);
      toast.error(formatApiError(err));
    } finally {
      if (isCurrentOperation()) {
        if (sequence === dashboardSequence.current && isSessionCurrent(scope)) setLoading(false);
        await shippingCheckRequest;
      }
    }
  }, [loadShippingChecks, beginOperation]);

  useEffect(() => {
    const scope = captureSession();
    const reset = () => { orderSelection.current += 1; dashboardSequence.current += 1; shippingCheckSequence.current += 1; setQuotes([]); setNotifications([]); setFulfillmentAlerts(null); setFulfillmentIssueSnapshot(null); setCommerceOrders([]); setSelectedOrder(null); setShippingChecks(null); setGalleryItems([]); setFeaturedIds([]); setEvents([]); setHasLoaded(false); };
    window.addEventListener("bbw:session-change", reset);
    load();
    Promise.all([api.get("/admin/gallery"), api.get("/admin/featured"), api.get("/admin/events")])
      .then(([galleryResponse, featuredResponse, eventsResponse]) => {
        if (!isSessionCurrent(scope)) return;
        setGalleryItems(galleryResponse.data || []);
        setFeaturedIds(featuredResponse.data?.gallery_ids || []);
        setEvents(eventsResponse.data || []);
      })
      .catch((err) => { if (isSessionCurrent(scope)) toast.error(formatApiError(err)); });
    const pollId = window.setInterval(load, 30000);
    return () => { window.removeEventListener("bbw:session-change", reset); window.clearInterval(pollId); dashboardSequence.current += 1; shippingCheckSequence.current += 1; };
  }, [load]);

  const reconcileRefund = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    setReconcileBusy(true);
    try {
      const response = await api.post(`/admin/orders/${selectedOrder.id}/refund/reconcile`, {});
    if (!isCurrentOperation()) return;
      toast.success(`Square refund status: ${response.data.refund_status || "unknown"}.`);
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    } finally {
      if (isCurrentOperation()) {
        setReconcileBusy(false);
      }
    }
  };

  const updateStatus = async (id, status) => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    try {
      await api.patch(`/admin/quotes/${id}/status`, { status });
    if (!isCurrentOperation()) return;
      toast.success(`Updated to ${status}`);
      load();
    } catch (err) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(err));
    }
  };

  const triggerDownload = (url, filename) => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((response) => {
        if (!isCurrentOperation()) throw new Error("Session changed");
        if (!response.ok) throw new Error("download failed");
        return response.blob();
      })
      .then((blob) => {
        if (!isCurrentOperation()) return;
        const anchor = document.createElement("a");
        anchor.href = URL.createObjectURL(blob);
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(anchor.href);
      })
      .catch(() => { if (isCurrentOperation()) toast.error("Download failed"); });
  };

  const validateApprovedTotal = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const normalized = String(value).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
      return null;
    }
    const amount = Number(normalized);
    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }
    return amount;
  };

  const saveApprovedTotal = async (quote) => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    const rawValue =
      approvedTotalDrafts[quote.id] ?? quote.approved_total ?? "";
    const amount = validateApprovedTotal(rawValue);
    if (amount === null) {
      toast.error(
        "Enter a valid approved total greater than zero with up to two decimal places.",
      );
      return;
    }

    try {
      await api.patch(`/admin/quotes/${quote.id}/status`, {
        status: quote.status,
        approved_total: amount,
      });
    if (!isCurrentOperation()) return;
      toast.success("Approved total saved.");
      setApprovedTotalDrafts((current) => ({ ...current, [quote.id]: "" }));
      load();
    } catch (err) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(err));
    }
  };

  const createPaymentLink = async (quote) => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return;
    const approvedTotal = Number(quote.approved_total ?? 0);
    const isReady =
      quote.status === "Approved" &&
      Number.isFinite(approvedTotal) &&
      approvedTotal > 0;
    if (!isReady) {
      toast.error(
        "Approve the quote and save a valid approved total before creating the Square link.",
      );
      return;
    }

    try {
      const response = await api.post(
        `/admin/quotes/${quote.id}/payment-link`,
        {},
      );
    if (!isCurrentOperation()) return;
      toast.success("Square payment link created.");
      load();
      if (response.data?.url) {
        window.open(response.data.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(err));
    }
  };

  const openOrder = async (orderId) => {
    orderSelection.current += 1;
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    try {
      const response = await api.get(`/admin/orders/${orderId}`);
    if (!isCurrentOperation()) return;
      setSelectedOrder(response.data);
      setOrderActionReason("");
      setRefundPreview(null);
      setProductionAcknowledged(false);
      setShippingRates([]);
      setShippingQuoteId("");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  useEffect(() => {
    if (!requestedOrder) return;
    let active = true;
    orderSelection.current += 1;
    const current = beginOperation();
    const selection = orderSelection.current;
    const isCurrentOperation = () => current() && selection === orderSelection.current;
    api.get(`/admin/orders/${encodeURIComponent(requestedOrder)}`).then((response) => {
      if (response.data?.id !== requestedOrder) throw new Error("The requested order could not be verified.");
      if (active && isCurrentOperation()) { setSelectedOrder(response.data); setOrderActionReason(""); setRefundPreview(null); setProductionAcknowledged(false); setShippingRates([]); setShippingQuoteId(""); }
    }).catch((error) => { if (active && isCurrentOperation()) toast.error(formatApiError(error)); });
    return () => { active = false; };
  }, [requestedOrder, beginOperation]);

  const cancelOrder = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (!orderActionReason.trim()) return toast.error("Enter a cancellation reason.");
    if (!window.confirm("Cancel this unpaid order and release its reserved inventory?")) return;
    try {
      const response = await api.post(`/admin/orders/${selectedOrder.id}/cancel`, { confirmation: true, reason: orderActionReason.trim(), idempotency_key: `cancel:${selectedOrder.id}:${crypto.randomUUID()}` });
    if (!isCurrentOperation()) return;
      toast.success(`Order canceled. ${response.data.reservations_released || 0} reserved item(s) released.`);
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const previewRefund = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    try {
      const response = await api.post(`/admin/orders/${selectedOrder.id}/refund/preview`, {});
    if (!isCurrentOperation()) return;
      setRefundPreview(response.data);
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const refundOrder = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (!orderActionReason.trim()) return toast.error("Enter a refund reason.");
    if (!window.confirm("Issue a full refund for the backend-authoritative paid total?")) return;
    try {
      const response = await api.post(`/admin/orders/${selectedOrder.id}/refund`, { confirmation: true, reason: orderActionReason.trim(), idempotency_key: `refund:${selectedOrder.id}:${crypto.randomUUID()}`, production_started_acknowledged: productionAcknowledged });
    if (!isCurrentOperation()) return;
      toast.success(response.data.status === "refunded" ? "Refund completed." : "Refund requested. Refresh to check its status.");
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const completeOrder = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (!window.confirm("Mark this order complete? This finishes all production work and clears its production alerts.")) return;
    try {
      await api.post(`/admin/orders/${selectedOrder.id}/complete`, { confirmation: true });
    if (!isCurrentOperation()) return;
      toast.success("Order completed.");
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const refreshShippingRates = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    setShippingBusy(true);
    try {
      const response = await api.post(`/admin/orders/${selectedOrder.id}/shipping/rates`, {});
    if (!isCurrentOperation()) return;
      setShippingRates(response.data.choices || []);
      setShippingQuoteId(response.data.choices?.[0]?.id || "");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    } finally {
      if (isCurrentOperation()) {
        setShippingBusy(false);
      }
    }
  };

  const purchaseShippingLabel = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    const selectedRate = shippingRates.find((rate) => rate.id === shippingQuoteId);
    if (!selectedRate || !Number.isSafeInteger(selectedRate.provider_amount_minor) || selectedRate.provider_amount_minor <= 0) return toast.error("Select a current shipping rate first.");
    if (!selectedOrder.shipping_packages?.length) return toast.error("Verified parcel information is required before buying postage.");
    if (!window.confirm(`Purchase postage for ${selectedOrder.shipping_packages.length} parcel(s) for $${(selectedRate.provider_amount_minor / 100).toFixed(2)} now? Live postage will be charged to the shipping provider account. If a purchase is uncertain, check provider records before any further purchase.`)) return;
    setShippingBusy(true);
    try {
      await api.post(`/admin/orders/${selectedOrder.id}/shipping/label`, {
        shipping_quote_id: shippingQuoteId,
        confirmation: true,
        idempotency_key: `shipping-label:${selectedOrder.id}:${crypto.randomUUID()}`,
      });
    if (!isCurrentOperation()) return;
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
      toast.success("Postage results recorded. Verify every parcel label; the order has not been marked shipped.");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
    } finally {
      if (isCurrentOperation()) {
        setShippingBusy(false);
      }
    }
  };

  const markShippingShipped = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (!window.confirm("Confirm every parcel has been handed to the carrier? This sends the tracking email for all parcels.")) return;
    try {
      await api.post(`/admin/orders/${selectedOrder.id}/shipping/mark-shipped`, { confirmation: true });
    if (!isCurrentOperation()) return;
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
      toast.success("Order marked shipped.");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const resendOrderEmail = async (template, label) => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (!window.confirm(`Resend the ${label} email to the customer? This does not charge the card or change the order.`)) return;
    try {
      await api.post(`/admin/orders/${selectedOrder.id}/emails/${template}/resend`);
    if (!isCurrentOperation()) return;
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
      toast.success(`${label} email re-sent.`);
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const voidShippingLabel = async () => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (!window.confirm("Request voiding this unused carrier label? Any postage refund is subject to carrier approval.")) return;
    try {
      await api.post(`/admin/orders/${selectedOrder.id}/shipping/void-label`, { confirmation: true, idempotency_key: `void-label:${selectedOrder.id}:${selectedOrder.shipping_label?.label_id}` });
    if (!isCurrentOperation()) return;
      await load();
    if (!isCurrentOperation()) return;
      await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return;
      toast.success("Label void request recorded.");
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const updatePersonalization = async (lineId, status) => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (
      status === "locked" &&
      !window.confirm(
        "Lock personalization for this item? The customer will no longer be able to edit it.",
      )
    )
      return;
    try {
      const response = await api.patch(
        `/admin/orders/${selectedOrder.id}/personalization`,
        { line_id: lineId, status, confirmation: status === "locked" },
      );
    if (!isCurrentOperation()) return;
      setSelectedOrder(response.data);
      await load();
    if (!isCurrentOperation()) return;
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  const updateProduction = async (lineId, status) => {
    const isCurrentOperation = beginOrderOperation();
    if (!isCurrentOperation()) return;
    if (
      status === "cutting" &&
      !window.confirm("Confirm this item is ready to enter cutting?")
    )
      return;
    try {
      const response = await api.patch(
        `/admin/orders/${selectedOrder.id}/production`,
        { line_id: lineId, status, confirmation: status === "cutting" },
      );
    if (!isCurrentOperation()) return;
      setSelectedOrder(response.data);
      await load();
    if (!isCurrentOperation()) return;
    } catch (error) {
    if (!isCurrentOperation()) return;
      toast.error(formatApiError(error));
    }
  };

  return (
    <div className="bbw-admin-dashboard min-h-screen" data-testid="admin-page">
      <section className="bbw-dashboard-header bbw-workbench-hero">
        <div className="bbw-hero-copy"><p className="bbw-dashboard-eyebrow">Beyond Board / Business workspace</p><h1>The workbench</h1><p className="bbw-hero-tagline">Crafted products. Streamlined operations.</p>
        <div className="bbw-hero-readiness"><strong>{shippingChecks && !shippingCheckError ? shippingMissing.length : "—"}</strong><h2>{shippingChecks && !shippingCheckError ? (shippingMissing.length === 0 ? "Shipping details are complete" : shippingMissing.length === 1 ? "item needs shipping details" : "items need shipping details") : "Shipping readiness needs verification"}</h2><p>{shippingCheckError ? "The latest check failed. Review previous results below." : shippingChecks && shippingMissing.length === 0 ? "Item data is complete. Review checkout and provider settings below." : "Assign a package and product-only weight."}</p><Link className="bbw-cream-action" to="/admin/shipping?filter=missing">Review items <span aria-hidden="true">→</span></Link></div></div>

        <dl className="bbw-hero-status"><div><dt>Ready</dt><dd>{shippingChecks && !shippingCheckError ? shippingChecks.items.filter(item=>item.shipping_ready).length : "—"}</dd></div><div><dt>To review</dt><dd>{shippingChecks && !shippingCheckError ? shippingMissing.length : "—"}</dd></div>{[["Shipping","shipping_enabled"],["Pickup","pickup_enabled"]].map(([label,key])=><div key={key}><dt>{label}</dt><dd className={shippingCheckError || typeof shippingChecks?.settings?.[key] !== "boolean" ? "is-unknown" : shippingChecks.settings[key] ? "is-on" : "is-off"}>{shippingCheckError || typeof shippingChecks?.settings?.[key] !== "boolean" ? "Unknown" : shippingChecks.settings[key] ? "On" : "Off"}</dd></div>)}</dl>
      </section>

      <section
        className="bbw-dashboard-body max-w-7xl mx-auto px-6 lg:px-10 pb-20"
        data-testid="admin-quotes-list"
      >
        <div className="bbw-workspace-tools"><details className="bbw-dashboard-account"><summary>Workspace account</summary><p>Signed in as {user?.email}</p></details><Button variant="ghost" onClick={load} data-testid="admin-refresh">Refresh workspace</Button></div>
        {loadError && <div role="alert" className="bbw-dashboard-error"><strong>Dashboard could not refresh.</strong><p>{hasLoaded ? "Showing the last loaded information. " : "Order and inventory counts are unavailable. "}{loadError}</p><Button onClick={load}>Try again</Button></div>}
        {!hasLoaded && loading && <p role="status">Loading dashboard information…</p>}
        <div className="bbw-workbench-grid"><div className="bbw-workbench-left"><section className="bbw-business-insights" data-testid="bbw-insights-link-card"><div className="bbw-insights-heading"><h2>Business insights</h2><span>{BBW_INSIGHTS_ENABLED ? "Opens Client Studio" : "Connection pending"}</span></div><div className="bbw-insights-body"><h3>BBW reporting in Blair Creative Studio</h3><p>{BBW_INSIGHTS_SIGN_IN_NOTICE}</p><a className="bbw-fix-link" href={BBW_INSIGHTS_HREF} role="link" aria-disabled={!BBW_INSIGHTS_ENABLED || undefined} title={BBW_INSIGHTS_SIGN_IN_NOTICE}>{BBW_INSIGHTS_LABEL} <span aria-hidden="true">→</span></a></div></section><section className="bbw-product-readiness"><h2>Product readiness</h2><p>From the latest saved shipping check</p>{shippingChecks ? shippingChecks.items.slice(0,4).map((item,index)=><Link key={`${item.product_id}:${item.variant_id||index}`} to={inventoryItemRoute(item.product_id,item.variant_id)}><span className="bbw-product-mark" aria-hidden="true">{String(index+1).padStart(2,"0")}</span><span><strong>{item.product_name || "Product"}{item.variant_name ? ` · ${item.variant_name}` : ""}</strong><small>{item.shipping_ready ? "Shipping details ready" : "Shipping details need review"}</small></span><span aria-hidden="true">›</span></Link>) : <p>Readiness unavailable until the check completes.</p>}<Link className="bbw-all-products" to="/admin/inventory">View all products →</Link></section></div>
        <section className="bbw-priority-workspace" id="priority-center" data-testid="priority-center">
          <div className="bbw-priority-heading"><p className="bbw-dashboard-eyebrow">Priority center</p><h2>What needs attention</h2><p>Fix the item. Refresh to verify.</p></div>
          <div className="bbw-shipping-priorities" data-testid="shipping-priorities">
            <h3>Shipping readiness</h3>
            {shippingChecking && <p role="status">Checking saved shipping data…{shippingChecks ? " Previous results remain below until verification finishes." : ""}</p>}
            {shippingCheckError && <p role="alert">Shipping checks unavailable. {shippingChecks ? "Previous results are shown and may be out of date. " : "Readiness is unknown. "}{shippingCheckError}</p>}
            {shippingSetupIssues.length > 0 && <article className="bbw-priority-row"><div><strong>Review shipping setup</strong><ul className="bbw-priority-checklist">{shippingSetupIssues.flatMap((entry) => String(entry.reason).split(" · ")).map((reason,index) => <li key={`${index}:${reason}`}>{reason}</li>)}</ul></div><Link className="bbw-fix-link" aria-label="Review shipping provider settings" to="/admin/shipping?section=settings#shipping-settings">Fix this →</Link></article>}
            {shippingMissing.length > 0 && <div className="bbw-shipping-issue-list"><p className="bbw-shipping-issue-count">{shippingMissing.length} product / variant {shippingMissing.length === 1 ? "row needs" : "rows need"} shipping details</p>{shippingMissing.slice(0,3).map(renderShippingIssue)}{shippingMissing.length>3 && <details><summary>Show {shippingMissing.length-3} more items</summary>{shippingMissing.slice(3).map(renderShippingIssue)}</details>}<p>Issues clear only after saved data passes a fresh readiness check.</p></div>}

            {shippingChecks && !shippingChecking && !shippingCheckError && !shippingMissing.length && !shippingSetupIssues.length && <p className="bbw-priority-clear">Saved shipping data passes these readiness checks. Postage purchasing has its own safeguards.</p>}
          </div>
        {retainedVerificationIssues.length > 0 && <details className="bbw-shipping-issue-list"><summary>{retainedVerificationIssues.length} previously flagged issues need verification</summary><p>Acknowledging an alert is not proof that its underlying issue was fixed. These remain unverified until an authoritative check is connected.</p>{retainedVerificationIssues.map((entry) => <article className="bbw-priority-row" key={entry.key}><div><strong>{entry.title}</strong><p>{entry.reason}</p></div><Link className="bbw-fix-link" aria-label={`Verify ${entry.title}`} to={entry.actionRoute}>Check this →</Link></article>)}</details>}
        {fulfillmentAlerts && (
          <section className="mb-8 border-y border-[#D8A34B]/35 py-5" data-testid="order-priority-center">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase text-[#D8A34B]">
                  Orders & stock
                </p>
                <h2 className="font-display text-2xl text-stone-100">
                  Production priorities
                </h2>
              </div>
              {((fulfillmentAlerts.counts.lifecycle_critical || 0) + fulfillmentAlerts.counts.production_required + fulfillmentAlerts.counts.low_inventory + fulfillmentAlerts.counts.out_of_stock) > 0 && <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
                <span className="rounded-full border border-red-500/40 bg-red-950/50 px-3 py-1 text-red-200">
                  Critical {fulfillmentAlerts.counts.lifecycle_critical || 0}
                </span>
                <span className="rounded-full border border-amber-400/40 bg-amber-950/50 px-3 py-1 text-amber-200">
                  Production {fulfillmentAlerts.counts.production_required}
                </span>
                <span className="rounded-full border border-yellow-400/40 bg-yellow-950/50 px-3 py-1 text-yellow-200">
                  Low stock {fulfillmentAlerts.counts.low_inventory}
                </span>
                <span className="rounded-full border border-stone-100/15 bg-zinc-900 px-3 py-1 text-stone-300">
                  Out {fulfillmentAlerts.counts.out_of_stock}
                </span>
              </div>}
            </div>
            <div className="mt-4 space-y-3">
              {(fulfillmentAlerts.lifecycle_critical || []).length > 0 && (
                <PriorityAccordion
                  id="priority-critical"
                  title="Critical"
                  count={fulfillmentAlerts.lifecycle_critical.length}
                  tone="critical"
                >
                  {fulfillmentAlerts.lifecycle_critical.map((alert) => (
                    <article
                      key={alert.id}
                      className="grid gap-3 border-l-4 border-red-500 bg-red-950/25 p-3 sm:grid-cols-[1fr_auto] sm:items-center"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-red-100">{alert.title}</p>
                        <p className="text-sm text-red-100/80">{alert.message}</p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button className="w-full sm:w-auto" onClick={() => openOrder(alert.order_id)}>
                          Open Order
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full sm:w-auto bg-transparent border-red-500/40 text-red-200 hover:bg-red-950/40"
                          disabled={ackBusy === alert.id}
                          onClick={() => acknowledgeAlert(alert.id)}
                          data-testid={`acknowledge-${alert.id}`}
                        >
                          {ackBusy === alert.id ? "Acknowledging..." : "Acknowledge"}
                        </Button>
                      </div>
                    </article>
                  ))}
                </PriorityAccordion>
              )}
              {fulfillmentAlerts.production_required.length > 0 && (
                <PriorityAccordion
                  id="priority-production"
                  title="Production"
                  count={fulfillmentAlerts.production_required.length}
                  tone="action"
                >
                  {fulfillmentAlerts.production_required.map((alert) => (
                    <PriorityOrderCard
                      key={alert.id}
                      alert={alert}
                      onOpen={openOrder}
                      onAcknowledge={acknowledgeAlert}
                      acknowledgeBusy={ackBusy === alert.id}
                    />
                  ))}
                </PriorityAccordion>
              )}
              {fulfillmentAlerts.low_inventory.length > 0 && (
                <PriorityAccordion
                  id="priority-low-inventory"
                  title="Low inventory"
                  count={fulfillmentAlerts.low_inventory.length}
                  tone="warning"
                >
                  {fulfillmentAlerts.low_inventory.map((alert) => (
                    <article
                      key={`low-${alert.product_id}-${alert.variant_id || "product"}`}
                      className="grid gap-3 border-l-4 border-yellow-400 p-3 sm:grid-cols-[56px_1fr_auto] sm:items-center"
                    >
                      <AdminImageWithFallback
                        src={alert.image_url}
                        alt=""
                        className="h-14 w-14 rounded object-cover"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-100">
                          {alert.product}
                          {alert.variant ? ` — ${alert.variant}` : ""}
                        </p>
                        <p className="text-sm text-stone-400">
                          Available {alert.available} · Reorder threshold{" "}
                          {alert.reorder_threshold}
                        </p>
                      </div>
                      <Link aria-label={`Fix inventory for ${alert.product}${alert.variant ? ` ${alert.variant}` : ""}`} to={inventoryItemRoute(alert.product_id, alert.variant_id)}>
                        <Button variant="outline" className="w-full sm:w-auto">Open Inventory</Button>
                      </Link>
                    </article>
                  ))}
                </PriorityAccordion>
              )}
              {fulfillmentAlerts.out_of_stock.length > 0 && (
                <PriorityAccordion
                  id="priority-out-of-stock"
                  title="Out of stock"
                  count={fulfillmentAlerts.out_of_stock.length}
                  tone="neutral"
                >
                  <article className="flex flex-wrap items-center justify-between gap-3 border-l-4 border-stone-500 p-3">
                    <p className="text-sm text-stone-300">
                      {fulfillmentAlerts.out_of_stock.length}{" "}
                      {fulfillmentAlerts.out_of_stock.length === 1
                        ? "product is"
                        : "products are"}{" "}
                      out of stock. Restock from the inventory page when ready.
                    </p>
                    <div className="bbw-priority-item-links">{(fulfillmentIssueSnapshot?.issues || []).filter((entry) => entry.kind === "out_of_stock" && entry.status === "open").map((entry) => <Link className="bbw-fix-link" key={entry.key} to={entry.actionRoute}>{entry.title} · Fix this →</Link>)}</div>
                    <Link to="/admin/inventory?stock=out">
                      <Button variant="outline" className="w-full sm:w-auto">Manage Inventory</Button>
                    </Link>
                  </article>
                </PriorityAccordion>
              )}
              {(fulfillmentAlerts.needs_pricing || []).length > 0 && (
                <PriorityAccordion id="priority-needs-pricing" title="Needs pricing" count={fulfillmentAlerts.needs_pricing.length} tone="warning">
                  <article className="flex flex-wrap items-center justify-between gap-3 border-l-4 border-yellow-400 p-3">
                    <p className="text-sm text-stone-300">{fulfillmentAlerts.needs_pricing.length} products need prices.</p>
                    <div className="bbw-priority-item-links">{(fulfillmentIssueSnapshot?.issues || []).filter((entry) => entry.kind === "needs_pricing" && entry.status === "open").map((entry) => <Link className="bbw-fix-link" key={entry.key} to={entry.actionRoute}>{entry.title} · Fix this →</Link>)}</div>
                    <Link to="/admin/inventory?pricing=needs">
                      <Button variant="outline" className="w-full sm:w-auto">Manage Inventory</Button>
                    </Link>
                  </article>
                </PriorityAccordion>
              )}
              {!(fulfillmentAlerts.lifecycle_critical || []).length &&
                !fulfillmentAlerts.production_required.length &&
                !fulfillmentAlerts.low_inventory.length &&
                !fulfillmentAlerts.out_of_stock.length &&
                !(fulfillmentAlerts.needs_pricing || []).length && (
                  <p className="bbw-production-clear text-sm text-stone-400">
                    No active production or stock alerts returned. Other checks may still need attention.
                  </p>
                )}
            </div>
            <div className="mt-3">
              <button
                type="button"
                onClick={loadAlertHistory}
                aria-expanded={historyOpen}
                className="text-sm text-stone-400 underline underline-offset-4 hover:text-stone-200"
                data-testid="alert-history-toggle"
              >
                {historyOpen ? "Hide alert history" : "View alert history"}
              </button>
              {historyOpen && (
                <div className="mt-3 space-y-2 rounded-xl border border-stone-100/10 bg-zinc-900/60 p-4" data-testid="alert-history">
                  {alertHistory.length === 0 ? (
                    <p className="text-sm text-stone-400">No cleared or resolved alerts yet.</p>
                  ) : (
                    alertHistory.map((entry) => (
                      <div key={entry.id} className="border-l-2 border-stone-600 pl-3 text-sm">
                        <p className="text-stone-200">
                          <span className="font-semibold">{entry.title || entry.type}</span>
                          {entry.order_id ? ` · Order ${String(entry.order_id).slice(0, 8).toUpperCase()}` : ""}
                        </p>
                        {entry.message && <p className="text-stone-400">{entry.message}</p>}
                        <p className="text-xs text-stone-500">
                          {entry.status}
                          {entry.created_at ? ` · created ${new Date(entry.created_at).toLocaleString()}` : ""}
                          {entry.acknowledged_at ? ` · cleared ${new Date(entry.acknowledged_at).toLocaleString()}${entry.acknowledged_by ? ` by ${entry.acknowledged_by}` : ""}` : ""}
                          {entry.updated_at && !entry.acknowledged_at ? ` · updated ${new Date(entry.updated_at).toLocaleString()}` : ""}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </section>
        )}
        </section></div>
        {hasLoaded && <section className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="admin-summary-grid">
          <AdminSummaryCard
            title="Orders / Production"
            tone={(fulfillmentAlerts?.counts?.lifecycle_critical || 0) > 0 ? "critical" : (fulfillmentAlerts?.counts?.production_required || 0) > 0 ? "action" : "neutral"}
            stats={[
              { label: "Orders", value: commerceOrders.length },
              { label: "Need production", value: fulfillmentAlerts?.counts?.production_required ?? 0, valueClass: (fulfillmentAlerts?.counts?.production_required || 0) > 0 ? "text-amber-300" : "text-stone-200" },
              { label: "Critical", value: fulfillmentAlerts?.counts?.lifecycle_critical || 0, valueClass: (fulfillmentAlerts?.counts?.lifecycle_critical || 0) > 0 ? "text-red-300" : "text-stone-200" },
            ]}
          >
            <a href="#admin-order-browser" className="mt-auto block pt-4">
              <Button variant="outline" className="w-full bg-transparent border-stone-100/15 text-stone-200 hover:bg-stone-100/5">
                View orders
              </Button>
            </a>
          </AdminSummaryCard>
          <AdminSummaryCard
            title="Inventory"
            tone={(fulfillmentAlerts?.counts?.low_inventory || 0) > 0 ? "warning" : "neutral"}
            stats={[
              { label: "Products", value: fulfillmentAlerts?.counts?.total_products ?? "—" },
              { label: "Out of stock", value: fulfillmentAlerts?.counts?.out_of_stock ?? 0 },
              { label: "Needs pricing", value: fulfillmentAlerts?.counts?.needs_pricing ?? 0 },
              { label: "Low stock", value: fulfillmentAlerts?.counts?.low_inventory ?? 0, valueClass: (fulfillmentAlerts?.counts?.low_inventory || 0) > 0 ? "text-yellow-300" : "text-stone-200" },
            ]}
            actionTo="/admin/inventory?stock=attention"
            actionLabel="Manage Inventory"
          />
          <details className="bbw-dashboard-utilities"><summary>Content & marketing <span>Gallery, featured pieces, events and social</span></summary><div className="bbw-dashboard-utility-grid">
          <AdminSummaryCard
            title="Gallery"
            stats={[
              { label: "Pieces", value: galleryItems.length },
              { label: "Categories", value: [...new Set(galleryItems.flatMap((item) => item.categories || [item.category]).filter(Boolean))].length },
            ]}
            actionTo="/admin/gallery"
            actionLabel="Manage Gallery"
          />
          <AdminSummaryCard
            title="Featured Pieces"
            stats={[{ label: "Currently featured", value: featuredIds.length }]}
            actionTo="/admin/gallery"
            actionLabel="Manage Gallery"
          >
            {galleryItems.filter((item) => featuredIds.includes(item.id)).length > 0 && (
              <div className="mt-3 flex gap-2">
                {galleryItems
                  .filter((item) => featuredIds.includes(item.id))
                  .slice(0, 3)
                  .map((item) => (
                    <AdminImageWithFallback
                      key={item.id}
                      src={item.image_url}
                      alt={item.title || ""}
                      className="h-12 w-12 rounded object-cover"
                    />
                  ))}
              </div>
            )}
          </AdminSummaryCard>
          <AdminSummaryCard
            title="Events"
            stats={[{ label: "Events", value: events.length }]}
            actionTo="/admin/events"
            actionLabel="Manage Events"
          />
          <AdminSummaryCard
            title="Shipping"
            actionTo="/admin/shipping"
            actionLabel="Shipping Setup"
          >
            <p className="mt-2 text-sm text-stone-400">
              Carriers, packages, and label settings.
            </p>
          </AdminSummaryCard>
          <AdminSummaryCard
            title="Social"
            actionTo="/admin/settings/social-accounts"
            actionLabel="Social Accounts"
          >
            <p className="mt-2 text-sm text-stone-400">
              Connected social accounts.
            </p>
          </AdminSummaryCard>
          </div></details>
        </section>}
        {commerceOrders.length > 0 && (
          <section id="admin-order-browser" className="mb-8 scroll-mt-6 border-y border-stone-100/10 py-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase text-stone-400">Commerce orders</p>
                <h2 className="font-display text-2xl text-stone-100">Order actions</h2>
              </div>
              <select
                value={selectedOrder?.id || ""}
                onChange={(event) => event.target.value && openOrder(event.target.value)}
                aria-label="Choose a commerce order" className="bbw-order-picker rounded-md border border-stone-100/15 bg-zinc-950 p-2.5 text-sm text-stone-100"
              >
                <option value="">Choose an order</option>
                {commerceOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.id.slice(0, 8).toUpperCase()} — {order.status}
                  </option>
                ))}
              </select>
            </div>
          </section>
        )}
        {selectedOrder && (
          <section className="bbw-selected-order mb-8 border-y border-[#26C6D5]/30 py-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase text-[#26C6D5]">
                  Production order
                </p>
                <h2 className="font-display text-2xl text-stone-100">
                  {selectedOrder.id.slice(0, 8).toUpperCase()}
                </h2>
                <p className="text-sm text-stone-400">
                  {selectedOrder.customer_name || selectedOrder.customer_email}{" "}
                  · {selectedOrder.fulfillment_method}
                </p>
              </div>
              <Button variant="ghost" onClick={() => { orderSelection.current += 1; setSelectedOrder(null); }}>
                Close
              </Button>
            </div>
            <div className="mt-5 border-t border-stone-100/10 pt-4">
              <p className="text-sm text-stone-300">
                Order status: <strong>{selectedOrder.status.replaceAll("_", " ")}</strong>
                {selectedOrder.refund_status ? ` · Refund: ${selectedOrder.refund_status}` : ""}
              </p>
              {selectedOrder.refund_status === "COMPLETED" && (
                <div className="mt-3 border-l-2 border-emerald-500 pl-4 text-sm text-stone-300" data-testid="admin-order-refund-completed">
                  <p>
                    Refund completed: ${((selectedOrder.refund_amount_minor || 0) / 100).toFixed(2)}{" "}
                    {selectedOrder.refund_currency || "USD"}
                    {selectedOrder.refund_completed_at
                      ? ` · ${new Date(selectedOrder.refund_completed_at).toLocaleString()}`
                      : ""}
                  </p>
                  {selectedOrder.square_refund_id && (
                    <p className="text-xs text-stone-500">Square refund {selectedOrder.square_refund_id}</p>
                  )}
                  {selectedOrder.inventory_restoration_status === "failed" && (
                    <p role="alert" className="mt-1 text-amber-300">
                      Inventory restock needs review — the refund itself is complete at Square.
                    </p>
                  )}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="admin-order-email-actions">
                <span className="text-xs uppercase text-stone-500">Resend email:</span>
                {["paid", "paid_review_required"].includes(selectedOrder.status) && (
                  <Button size="sm" variant="outline" onClick={() => resendOrderEmail("new_paid_order", "payment confirmation")}>
                    Resend confirmation
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => resendOrderEmail("in_production", "in-production")}>
                  In production
                </Button>
                {selectedOrder.fulfillment_method === "pickup" && (
                  <Button size="sm" variant="outline" onClick={() => resendOrderEmail("ready_for_pickup", "ready-for-pickup")}>
                    Ready for pickup
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => resendOrderEmail("completed", "completed")}>
                  Completed
                </Button>
              </div>
              {selectedOrder.fulfillment_method === "shipping" && (
                <section className="mt-5 border-t border-stone-100/10 pt-4" data-testid="admin-order-shipping">
                  <h3 className="font-display text-xl text-stone-100">Shipment</h3>
                  <p className="mt-1 text-sm text-stone-300">Status: {String(selectedOrder.shipping_status || "awaiting_payment").replaceAll("_", " ")} · Address: {String(selectedOrder.shipping_address_validation || "not validated").replaceAll("_", " ")}</p>
                  {selectedOrder.shipping_status === "address_review" && <p role="alert" className="mt-2 border-l-4 border-red-500 bg-red-950/25 p-3 text-sm text-red-100">The shipping address changed after the rate was calculated. Review the corrected address and re-rate before purchasing a label.</p>}
                  {selectedOrder.shipping_status === "address_review" && <ShippingAddressCorrection key={selectedOrder.id} order={selectedOrder} busy={shippingBusy} setBusy={setShippingBusy} onSaved={() => openOrder(selectedOrder.id)} />}
                  <address className="mt-3 not-italic text-sm text-stone-300">
                    {selectedOrder.shipping_address?.name}<br />
                    {selectedOrder.shipping_address?.address_1} {selectedOrder.shipping_address?.address_2}<br />
                    {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}<br />
                    {selectedOrder.shipping_address?.country}
                  </address>
                  <p className="mt-2 text-sm text-stone-400">Paid shipping: ${((selectedOrder.shipping_selection?.quoted_shipping_minor || 0) / 100).toFixed(2)} · Package count: {selectedOrder.shipping_packages?.length || 0}</p>
                  {(selectedOrder.shipping_packages || []).map((pack, index) => <p key={`package-${index}`} className="text-xs text-stone-500">Package {index + 1}: {pack.weight_oz} oz · {pack.dimensions_in?.length_in} × {pack.dimensions_in?.width_in} × {pack.dimensions_in?.height_in} in</p>)}
                  {selectedOrder.lines?.some((line) => line.production_required && !["ready", "completed"].includes(line.production_status)) && <p className="mt-2 text-sm text-amber-300">Made-to-order items must be marked ready before buying postage.</p>}
                  {(selectedOrder.shipping_label_purchase_intent || selectedOrder.shipping_label?.tracking_number) ? (
                    <>
                      <AdminShipmentLabels
                        order={selectedOrder}
                        busy={shippingBusy}
                        onRefresh={async () => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return; await load();
    if (!isCurrentOperation()) return; await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return; }}
                        onMarkShipped={markShippingShipped}
                        onVoid={voidShippingLabel}
                      />
                      {selectedOrder.shipping_email_status === "retryable" && <Button size="sm" variant="outline" onClick={async () => {
    const isCurrentOperation = beginOperation();
    if (!isCurrentOperation()) return; await api.post(`/admin/orders/${selectedOrder.id}/shipping/retry-email`, {});
    if (!isCurrentOperation()) return; await load();
    if (!isCurrentOperation()) return; await openOrder(selectedOrder.id);
    if (!isCurrentOperation()) return; }}>Retry tracking email</Button>}
                    </>
                  ) : ["paid", "paid_review_required"].includes(selectedOrder.status) && selectedOrder.shipping_status !== "address_review" && (
                    <div className="mt-3 space-y-2">
                      <Button variant="outline" disabled={shippingBusy} onClick={refreshShippingRates}>{shippingBusy ? "Checking rates..." : "Recalculate live rates"}</Button>
                      {shippingRates.map((rate) => <label key={rate.id} className="flex cursor-pointer items-center gap-2 border border-stone-100/10 p-2 text-sm"><input type="radio" name="admin-shipping-rate" checked={shippingQuoteId === rate.id} onChange={() => setShippingQuoteId(rate.id)} /><span className="flex-1">{rate.carrier_name} · {rate.service_name} · Customer shipping ${(rate.amount_minor / 100).toFixed(2)}{Number.isSafeInteger(rate.provider_amount_minor) ? ` · Postage $${(rate.provider_amount_minor / 100).toFixed(2)}` : " · Refresh to verify postage"}{rate.label ? ` · ${rate.label}` : ""}</span></label>)}
                      <Button disabled={shippingBusy || !shippingQuoteId} onClick={purchaseShippingLabel}>Preview and confirm label purchase</Button>
                    </div>
                  )}
                </section>
              )}
              <label className="bbw-field-label">{selectedOrder.status === "pending_payment" ? "Cancellation reason" : "Full refund reason"}<textarea
                aria-label={selectedOrder.status === "pending_payment" ? "Cancellation reason" : "Full refund reason"}
                value={orderActionReason}
                onChange={(event) => setOrderActionReason(event.target.value)}
                placeholder={selectedOrder.status === "pending_payment" ? "Cancellation reason" : "Full refund reason"}
                className="mt-3 min-h-20 w-full rounded-md border border-stone-100/15 bg-zinc-950 p-3 text-sm text-stone-100"
              /></label>
              {selectedOrder.status === "pending_payment" && (
                <Button className="mt-3" variant="outline" onClick={cancelOrder}>
                  Cancel Order
                </Button>
              )}
              {["paid", "refund_failed"].includes(selectedOrder.status) && (
                <div className="mt-3">
                  <Button variant="outline" onClick={previewRefund}>
                    Preview Full Refund
                  </Button>
                  {refundPreview && (
                    <div className="mt-3 border-l-2 border-[#D8A34B] pl-4 text-sm text-stone-300">
                      <p>
                        Full refund: ${(refundPreview.refundable_amount_minor / 100).toFixed(2)} {refundPreview.currency}
                      </p>
                      {refundPreview.inventory_effects.map((effect) => (
                        <p key={effect.line_id}>
                          {effect.product}{effect.variant ? ` — ${effect.variant}` : ""}: {effect.effect === "restore_ready_stock" ? "return to ready inventory after confirmed refund" : "manual physical disposition review"}
                        </p>
                      ))}
                      {refundPreview.requires_production_acknowledgement && (
                        <label className="mt-2 flex items-center gap-2">
                          <input type="checkbox" checked={productionAcknowledged} onChange={(event) => setProductionAcknowledged(event.target.checked)} />
                          I understand production or fulfillment has already started.
                        </label>
                      )}
                      <Button className="mt-3" onClick={refundOrder} disabled={refundPreview.requires_production_acknowledgement && !productionAcknowledged}>
                        Confirm Full Refund
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {["refund_pending", "refund_failed"].includes(selectedOrder.status) && selectedOrder.square_refund_id && (
                <Button className="mt-3" variant="outline" disabled={reconcileBusy} onClick={reconcileRefund} data-testid="admin-reconcile-refund">
                  {reconcileBusy ? "Checking with Square..." : "Check refund status with Square"}
                </Button>
              )}
              {selectedOrder.status === "paid" && (
                <div className="mt-3">
                  <Button variant="outline" onClick={completeOrder} data-testid="admin-complete-order">
                    Mark order complete
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-4">
              {selectedOrder.lines
                .filter((line) => line.production_required)
                .map((line) => (
                  <article
                    key={line.line_id}
                    className="grid gap-4 border-t border-stone-100/10 pt-4 md:grid-cols-[96px_1fr]"
                  >
                    <AdminImageWithFallback
                      src={line.image_url_snapshot}
                      alt=""
                      className="h-24 w-24 rounded object-cover"
                    />
                    <div>
                      <h3 className="text-lg font-semibold text-stone-100">
                        {line.product_name_snapshot}
                        {line.variant_name_snapshot
                          ? ` — ${line.variant_name_snapshot}`
                          : ""}
                      </h3>
                      <p className="text-sm text-stone-400">
                        Quantity {line.quantity} ·{" "}
                        {line.production_status.replaceAll("_", " ")} ·{" "}
                        {line.personalization_status.replaceAll("_", " ")}
                      </p>
                      <p className="mt-2 text-sm text-stone-300">
                        Original preview:{" "}
                        {JSON.stringify(line.checkout_customization || {})}
                      </p>
                      <p className="mt-1 text-sm text-stone-300">
                        Requested changes:{" "}
                        {JSON.stringify(line.personalization_request || {})}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          onClick={() =>
                            updatePersonalization(line.line_id, "approved")
                          }
                        >
                          Approve personalization
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() =>
                            updatePersonalization(
                              line.line_id,
                              "changes_requested",
                            )
                          }
                        >
                          Request clarification
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() =>
                            updatePersonalization(line.line_id, "locked")
                          }
                        >
                          Lock personalization
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() =>
                            updateProduction(line.line_id, "cutting")
                          }
                        >
                          Start cutting
                        </Button>
                        <select
                          aria-label={`Production status for ${line.product_name_snapshot}`}
                          value={line.production_status}
                          onChange={(event) =>
                            updateProduction(line.line_id, event.target.value)
                          }
                          className="rounded-md border border-stone-100/15 bg-zinc-950 px-3 text-sm text-stone-100"
                        >
                          <option value="queued">Queued</option>
                          <option value="finishing">Finishing</option>
                          <option value="ready">Ready</option>
                          <option value="shipped">Shipped</option>
                          <option value="completed">Completed</option>
                          <option value="canceled">Canceled</option>
                        </select>
                        <a href={`mailto:${selectedOrder.customer_email}`}>
                          <Button variant="outline">
                            <Mail className="mr-2 h-4 w-4" /> Contact customer
                          </Button>
                        </a>
                      </div>
                      {line.status_history?.length > 0 && (
                        <div className="mt-3 text-sm text-stone-400">
                          Status history:{" "}
                          {line.status_history
                            .map((entry) => entry.type)
                            .join(" · ")}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
            </div>
          </section>
        )}
        <section className="mb-8 border-y border-[#D8A34B]/30 bg-[#D8A34B]/5 py-5">
          <button
            type="button"
            onClick={() => setOrderAlertsOpen((current) => !current)}
            aria-expanded={orderAlertsOpen}
            data-testid="production-alerts-toggle"
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <span className="flex items-center gap-3">
              <p className="text-xs uppercase text-[#D8A34B]">
                Production alerts
              </p>
              <span className="rounded-full border border-[#D8A34B]/40 bg-[#D8A34B]/10 px-2 py-0.5 text-xs text-[#F5F0E6]">
                {orderAlertCount}
              </span>
            </span>
            <ChevronDown aria-hidden="true" className={`h-4 w-4 text-[#D8A34B] transition-transform ${orderAlertsOpen ? "rotate-180" : ""}`} />
          </button>
          {!orderAlertsOpen && orderAlertCount > 0 && (
            <p className="mt-2 text-sm text-stone-400">
              {selectedOrder
                ? `${selectedOrderNotifications.length} active alert${selectedOrderNotifications.length === 1 ? "" : "s"} for this order.`
                : `${activeNotifications.length} active alert${activeNotifications.length === 1 ? "" : "s"} across ${new Set(activeNotifications.map((n) => n.order_id)).size} order${new Set(activeNotifications.map((n) => n.order_id)).size === 1 ? "" : "s"}.`}
            </p>
          )}
          {orderAlertsOpen && (
            <div className="mt-3 space-y-2">
              {(selectedOrder ? selectedOrderNotifications : activeNotifications).length === 0 ? (
                <p className="rounded-md border border-stone-100/10 bg-zinc-950/40 px-4 py-3 text-sm text-stone-400">
                  {selectedOrder ? "No active alerts for this order." : "No active production alerts."}
                </p>
              ) : (
                (selectedOrder ? selectedOrderNotifications : activeNotifications).map((notification) => (
                  <article
                    key={notification.id}
                    className="border-l-2 border-[#D8A34B] pl-4"
                  >
                    <h2 className="text-base font-semibold text-stone-100">
                      {notification.title}
                    </h2>
                    <p className="text-sm text-stone-300">
                      {notification.message}
                    </p>
                    <p className="text-sm text-stone-400">
                      Customer: {notification.customer_email}
                    </p>
                  </article>
                ))
              )}
            </div>
          )}
        </section>
        {loading && <p className="text-stone-400">Loading…</p>}
        <div className="bbw-quote-heading"><div><p className="bbw-dashboard-eyebrow">Custom projects</p><h2>Quote requests</h2></div><label className="bbw-field-label">Find a request<input aria-label="Search quote requests" value={quoteSearch} onChange={(event) => setQuoteSearch(event.target.value)} placeholder="Customer, project or request ID" /></label></div>
        <div className="bbw-quote-tabs mb-5 flex flex-wrap items-center justify-between gap-3" data-testid="quote-tabs">
          <div className="flex flex-wrap gap-2">
            {[["active", `Active ${quoteCounts.active}`], ["completed", `Completed ${quoteCounts.completed}`], ["archived", `Archived ${quoteCounts.archived}`], ["all", `All ${quoteCounts.all}`]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setQuoteTab(key)}
                data-testid={`quote-tab-${key}`}
                aria-pressed={quoteTab === key}
                className={`rounded-full border px-4 py-2 text-sm transition ${quoteTab === key ? "border-[#D8A34B] bg-[#D8A34B]/15 text-[#F5F0E6]" : "border-stone-100/15 text-stone-300 hover:bg-stone-100/5"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <details className="bbw-dashboard-maintenance"><summary>Maintenance tools</summary><p>Only identified test quotes are eligible. You must review and confirm deletion.</p><Button variant="outline" onClick={previewTestCleanup} disabled={cleanupBusy} className="bg-transparent border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid="cleanup-test-quotes">
            {cleanupBusy ? "Scanning..." : "Clean up test quotes"}
          </Button></details>
        </div>
        {cleanupPreview && (
          <div className="mb-5 rounded-2xl border border-rose-500/40 bg-rose-950/20 p-4" data-testid="cleanup-preview">
            <p className="font-semibold text-rose-100">
              {cleanupPreview.count} test quote{cleanupPreview.count === 1 ? "" : "s"} will be permanently deleted.
            </p>
            {cleanupPreview.count > 0 && (
              <>
                <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto text-sm text-rose-100/80">
                  {cleanupPreview.candidates.map((c) => (
                    <li key={c.id}>
                      {c.id.slice(0, 8)} — {c.customer_name} · {c.customer_email} · {c.status} · {c.reasons.join("; ")}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    aria-label="Type DELETE TEST QUOTES to confirm cleanup"
                    value={cleanupConfirmText}
                    onChange={(e) => setCleanupConfirmText(e.target.value)}
                    placeholder="Type DELETE TEST QUOTES"
                    className="bbw-cleanup-confirm rounded-md border border-rose-500/40 bg-zinc-950 p-2 text-sm text-stone-100"
                    data-testid="cleanup-confirm-input"
                  />
                  <Button onClick={executeTestCleanup} disabled={cleanupBusy || cleanupConfirmText !== "DELETE TEST QUOTES"} className="bg-rose-600 text-white hover:bg-rose-500" data-testid="cleanup-confirm-button">
                    {cleanupBusy ? "Deleting..." : "Delete test quotes"}
                  </Button>
                  <Button variant="ghost" onClick={() => setCleanupPreview(null)} className="text-stone-300">
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        {hasLoaded && !loading && filteredQuotes.length === 0 && (
          <p className="text-stone-400">{quoteSearch.trim() ? "No requests match your search." : quoteTab === "active" ? "No active quote requests." : "No quotes in this view."}</p>
        )}
        <div className="space-y-5">
          {filteredQuotes.map((quote) => (
            <details
              key={quote.id}
              className="bbw-quote-card rounded-2xl border border-stone-100/10 bg-zinc-900/60 p-6"
            >
              <summary><div className="bbw-quote-summary flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D8A34B]">
                    {quote.project_type}
                  </p>
                  <h2 className="font-display text-2xl text-stone-100">
                    {quote.customer_name}
                  </h2>
                  <p className="text-sm text-stone-400 flex items-center gap-3 mt-2 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {quote.customer_email}
                    </span>
                    {quote.customer_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {quote.customer_phone}
                      </span>
                    )}
                  </p>
                </div>
                <Badge className={`border ${statusColor(quote.status)}`}>
                  {quote.status}
                </Badge>
              </div><span className="bbw-quote-open-hint">Review details & actions</span></summary>

              <div className="bbw-quote-info grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-sm">
                <Info label="Requested size" value={quote.size} />
                <Info
                  label="Indoor / outdoor"
                  value={quote.indoor_outdoor || "—"}
                />
                <Info
                  label="Colors / theme"
                  value={quote.colors_theme || "—"}
                />
                <Info
                  label="Personalization"
                  value={quote.personalization_text || "—"}
                />
                <Info label="Deadline" value={quote.deadline || "—"} />
                <Info label="Budget range" value={quote.budget_range || "—"} />
                <Info label="Status" value={quote.status} />
                <Info
                  label="Submitted"
                  value={new Date(quote.created_at).toLocaleString()}
                />
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                    Project description
                  </p>
                  <p className="text-stone-300 whitespace-pre-wrap leading-relaxed mt-1">
                    {quote.description}
                  </p>
                </div>
                {quote.notes && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                      Customer notes
                    </p>
                    <p className="text-stone-300 whitespace-pre-wrap leading-relaxed mt-1">
                      {quote.notes}
                    </p>
                  </div>
                )}
              </div>

              {(quote.inspiration_filenames?.length > 0 ||
                quote.artwork_filenames?.length > 0) && (
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <UploadSection
                    title="Inspiration images"
                    files={quote.inspiration_filenames || []}
                    onDownload={(filename) =>
                      triggerDownload(
                        `${API}/admin/quotes/uploads/${filename}`,
                        filename,
                      )
                    }
                  />
                  <UploadSection
                    title="Logo / artwork uploads"
                    files={quote.artwork_filenames || []}
                    onDownload={(filename) =>
                      triggerDownload(
                        `${API}/admin/quotes/uploads/${filename}`,
                        filename,
                      )
                    }
                  />
                </div>
              )}

              <div className="mt-5 grid gap-4 md:grid-cols-[1fr_1.3fr] items-end">
                <div className="max-w-xs">
                  <p className="mb-2 text-xs uppercase tracking-[0.2em] text-stone-500">
                    Quote status
                  </p>
                  <Select
                    value={quote.status}
                    onValueChange={(value) => updateStatus(quote.id, value)}
                  >
                    <SelectTrigger
                      className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                      data-testid={`admin-status-select-${quote.id}`}
                      aria-label={`Quote status for ${quote.customer_name}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-stone-100/10 text-stone-100">
                      {STATUS_OPTIONS.map((status) => (
                        <SelectItem
                          key={status}
                          value={status}
                          className="focus:bg-[#D8A34B]/20 focus:text-[#F5F0E6]"
                        >
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-2xl border border-[#D8A34B]/25 bg-[#D8A34B]/5 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D8A34B] mb-3">
                    Square payment
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="bbw-field-label">Approved total (USD)<Input
                      aria-label={`Approved total for ${quote.customer_name}`}
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={
                        approvedTotalDrafts[quote.id] ??
                        quote.approved_total ??
                        ""
                      }
                      onChange={(event) =>
                        setApprovedTotalDrafts((current) => ({
                          ...current,
                          [quote.id]: event.target.value,
                        }))
                      }
                      className="bg-zinc-950/60 border-stone-100/10 text-stone-100 max-w-[180px]"
                      placeholder="0.00"
                    /></label>

                    <Button
                      type="button"
                      onClick={() => saveApprovedTotal(quote)}
                      className="bg-[#D8A34B] text-[#15191C] hover:bg-[#c8963f]"
                    >
                      Save amount
                    </Button>

                    <Button
                      type="button"
                      onClick={() => createPaymentLink(quote)}
                      disabled={
                        quote.status !== "Approved" ||
                        Number(quote.approved_total ?? 0) <= 0
                      }
                      className="bg-[#26C6D5] text-[#15191C] hover:bg-[#1ab7d2] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Create link
                    </Button>
                  </div>

                  {(quote.payment_link_url || quote.payment_status) && (
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-stone-300">
                      <span className="rounded-full border border-[#26C6D5]/35 bg-[#26C6D5]/10 px-2 py-1 text-[#26C6D5] uppercase tracking-[0.2em]">
                        {quote.payment_status || "pending"}
                      </span>
                      {quote.payment_link_url && (
                        <a
                          href={quote.payment_link_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#D8A34B] underline underline-offset-4"
                        >
                          Open payment page
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
        {label}
      </p>
      <p className="text-stone-200">{value}</p>
    </div>
  );
}

function UploadSection({ title, files, onDownload }) {
  if (!files.length) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#D8A34B] mb-2">
        {title}
      </p>
      <div className="flex flex-wrap gap-2">
        {files.map((filename) => (
          <Button
            key={filename}
            size="sm"
            variant="outline"
            className="bg-transparent border-stone-100/15 text-stone-200"
            onClick={() => onDownload(filename)}
          >
            <Download className="w-3 h-3 mr-1" /> {filename.slice(0, 18)}
          </Button>
        ))}
      </div>
    </div>
  );
}

